import {BaseElement, BaseElementAsync, BASIS, MsgLabel, OSkinableInit} from "back/commons/basis";
import {JRemoteResDescFromClient, REMOTE, RemoteRes} from "lib/core/remote";
import {Button, OButtonInit} from "back/commons/widgets/buttons";
import {DOMSH} from "lib/commons/xml/domsh";
import {DOM, JSX} from "lib/commons/xml/dom";
import {IReg, IRegPointer, REG} from "lib/commons/registry";
import {IBasicUniversePointer, ICoreUniverseEnv} from "lib/core/universe";
import {LANG} from "lib/commons/lang";
import {POPUP} from "back/commons/widgets/popups";
import {BarActions, OBarActionsInit} from "back/commons/widgets/bars";
import {Action} from "lib/commons/actions";

/**
 * Affiche une ressource distante RemoteRes.
 */
export interface RemoteResRenderer extends BaseElement {
	initialize(init: ORemoteResRendererInit): this
}

export interface ORemoteResRendererInit extends OSkinableInit {
	res?: RemoteRes | null
	/** 'embed' =  'img' | 'audio' | 'video' | 'iframe', s'oppose à 'window' et 'download'. */
	forceWebRender?: 'img' | 'audio' | 'video' | 'iframe' | 'embed' | 'window' | 'download';
}

export class RemoteResRenderer extends BaseElementAsync {
	config: ORemoteResRendererInit;
	resShown: RemoteRes | null;
	msgShown: { msg: string, level?: 'info' | 'error' | 'warning' | 'valid' | null, icon?: string } | null;
	webRender: 'img' | 'audio' | 'video' | 'iframe' | 'window' | 'download' | null;

	protected _renderTag: HTMLIFrameElement | HTMLImageElement | HTMLAudioElement | HTMLVideoElement | Button;
	protected _msgTag: MsgLabel;

	async setRemoteRes(res: RemoteRes | null) {
		if (res ? res.url === this.resShown?.url : this.resShown == null) return;
		this.resShown = res;
		this.msgShown = null;
		if (res) {
			if (this.config.forceWebRender && this.config.forceWebRender !== 'embed') {
				this.webRender = this.config.forceWebRender;
			} else {
				this.msgShown = {msg: "..."};
				this.refresh();
				const webRender = await res.getBestWebRendition();
				if (this.resShown !== res) return; //raceCond
				this.msgShown = null;
				if (this.config.forceWebRender === 'embed' && (webRender === "window" || webRender === "download")) {
					this.webRender = null; //On refuse ce rendu
				} else {
					this.webRender = webRender;
				}
			}
		}
		this.refresh();
	}

	setMsg(msg: string | null, level?: 'info' | 'error' | 'warning' | 'valid' | null, icon?: string) {
		this.resShown = null;
		this.webRender = null;
		this.msgShown = {msg, level, icon};
		this.refresh();
	}

	protected _initialize(init: ORemoteResRendererInit): Promise<void> {
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);
		this.config = init;
		this.config.forceWebRender;
		this._msgTag = this.shadowRoot.appendChild(<c-msg/>) as MsgLabel;
		return this.setRemoteRes(init.res);
	}

	protected showRenderTag(show: boolean) {
		DOM.setHidden(this._msgTag, show);
		if (this._renderTag) this._renderTag.hidden = !show;
		this.className = show ? this._renderTag?.className || "msg" : "msg";
	}

	protected _refresh() {
		const sr = this.shadowRoot;
		if (this._renderTag) {
			this._renderTag.remove();
			this._renderTag = null;
		}
		if (this.msgShown) {
			this.showRenderTag(false);
			this._msgTag.setCustomMsg(this.msgShown.msg, this.msgShown.level, this.msgShown.icon);
			return;
		}
		switch (this.resShown ? this.webRender : null) {
		case 'img':
			this._renderTag = sr.insertBefore(<img class="imgView" hidden="" src={this.resShown.url}/>, this._msgTag) as HTMLImageElement;
			break;
		case 'video':
			this._renderTag = sr.insertBefore(<video class="videoView" controls="controls" hidden="" src={this.resShown.url}/>, this._msgTag) as HTMLVideoElement;
			//TODO chercher le poster dans RemoteRes
			break;
		case 'audio':
			this._renderTag = sr.insertBefore(<audio class="audioView" controls="controls" hidden="" src={this.resShown.url}/>, this._msgTag) as HTMLAudioElement;
			break;
		case "iframe":
			this._renderTag = sr.insertBefore(<iframe class="htmlView" hidden="" sandbox="allow-scripts allow-same-origin allow-forms" allow="encrypted-media; picture-in-picture" src={this.resShown.url}/>, this._msgTag) as HTMLIFrameElement;
			break;
		case "download":
			this._renderTag = sr.insertBefore(<Button class="downloadBtn" hidden="" î={{reg: this.resShown.reg, label: "Télécharger", uiContext: "dialog"} as OButtonInit} onclick={this.onDownloadBtn}/>, this._msgTag) as Button;
			break;
		case "window":
			this._renderTag = sr.insertBefore(<Button class="openBtn" hidden="" î={{reg: this.resShown.reg, label: "Ouvrir...", uiContext: "dialog"} as OButtonInit} onclick={this.onOpenBtn}/>, this._msgTag) as Button;
			break;
		default :
			this._msgTag.setCustomMsg("Visualisation non disponible", "info");
		}
		if (this._renderTag) {
			if (!(this._renderTag instanceof Button)) this._renderTag.onerror = function (this: HTMLIFrameElement, ev: Event) {
				const me = DOMSH.findHost<RemoteResRenderer>(this)
				me.showRenderTag(false);
				me._msgTag.setCustomMsg("Ressource non disponible", "error");
			};
			this.showRenderTag(true);
		} else {
			this.showRenderTag(false);
		}
	}

	onDownloadBtn(this: Button, ev: MouseEvent) {
		const me = DOMSH.findHost<RemoteResRenderer>(this);
		const link = document.createElement('a');
		link.href = REMOTE.urlRemoteContent(me.resShown.url, me.config.reg); //via svc remote pour être sameDomain et que l'att download soit opératiobnnel.
		const path = new URL(me.resShown.url).pathname;
		const idx = path.lastIndexOf("/") + 1;
		link.setAttribute("download", idx > 0 ? path.substring(idx) : path);
		link.click();
	}

	onOpenBtn(this: Button, ev: MouseEvent) {
		const me = DOMSH.findHost<RemoteResRenderer>(this);
		window.open(me.resShown.url, "_blank");
	}

}

REG.reg.registerSkin('wsp-remoteres-renderer', 1, /* language=CSS */ `
	:host {
		flex: 1;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		text-align: center;
		vertical-align: middle;
		overflow: auto;
		display: flex;
	}


	:host > .imgView, :host > .videoView, :host > .audioView, :host > .iframeView, :host > .dynGenView {
		max-height: 100%;
		object-fit: scale-down;
		max-width: 100%;
	}

	:host(.itemPreview) > .imgView, :host(.itemPreview) > .videoView, :host(.itemPreview) > .iframeView, :host(.itemPreview) > .dynGenView {
		max-height: 10em;
	}

	:host > .audioView {
		max-height: 2em;
	}

	:host > .iframeView, :host > .dynGenView {
		border: 1px solid var(--border-color);
		width: 100%;
	}

	[hidden] {
		display: none;
	}

	iframe {
		width: 100%;
		height: 100%;
	  border: none;
  }

  c-msg {
	  margin: .5em;
	  font-style: italic;
  }
`);

customElements.define('wsp-remoteres-renderer', RemoteResRenderer);

/**
 * Affiche une ressource distante RemoteRes, avec éventuellement un titre et des actions
 */
export interface RemoteResRendererExtended<ACTCTX extends IRemoteResRendererActionCtx = IRemoteResRendererActionCtx> extends BaseElement {
	initialize(init: ORemoteResRendererExtendedInit<ACTCTX>): this
}

export interface ORemoteResRendererExtendedInit<CTX extends IRemoteResRendererActionCtx = IRemoteResRendererActionCtx> extends OSkinableInit {
	fetchResDesc: () => Promise<JRemoteResDescFromClient>
	remoteResSelector?: (reg: IReg<IBasicUniversePointer>, resDesc: JRemoteResDescFromClient) => RemoteRes | null
	/** Construction du titre. Si null ou ne retourne pas 'done', le titre =  RemoteRes.title || RemoteRes.url || "[url non renseignée]" */
	buildTitle?: ((this: RemoteResRendererExtended<CTX>, target: HTMLSpanElement, resMetas: JRemoteResDescFromClient | null) => 'done') | 'none'
	preview?: 'shown'/*par défaut*/ | 'none' | 'tooltip'
	previewInit?: ORemoteResRendererInit
	openBtn?: 'shown'/*par défaut*/ | 'none'
	actionsList?: string
}

export interface IRemoteResRendererActionCtx extends IRegPointer<ICoreUniverseEnv> {
	remoteDesc?: JRemoteResDescFromClient | null
	remoteResRenderer?: RemoteResRenderer
}

export class RemoteResRendererExtended<ACTCTX extends IRemoteResRendererActionCtx> extends BaseElement implements IRemoteResRendererActionCtx {
	public reg: IReg<ICoreUniverseEnv>

	protected config: ORemoteResRendererExtendedInit<ACTCTX>;

	protected titleRoot?: HTMLSpanElement;

	protected barActions: BarActions<ACTCTX>;

	remoteResRenderer?: RemoteResRenderer;

	remoteDesc?: JRemoteResDescFromClient | null;

	static ACTIONS_LIST_BASE: string = "actions:remoteResRendererExtended:base";

	protected _initialize(init: ORemoteResRendererExtendedInit<ACTCTX>) {
		this.reg = this.findReg(init);
		if (!init.remoteResSelector) {
			//Affectation du remoteResSelector.
			switch (init.previewInit?.forceWebRender) {
			case "download" :
			case "window" :
				init.remoteResSelector = (reg: IReg<IBasicUniversePointer>, resDesc: JRemoteResDescFromClient) => new RemoteRes(reg, resDesc);
				break;
			case "video" :
				init.remoteResSelector = REMOTE.remoteResAsVideo;
				break;
			case "audio" :
				init.remoteResSelector = REMOTE.remoteResAsAudio;
				break;
			default:
				init.remoteResSelector = REMOTE.remoteResForPreview;
			}
		}
		this.config = init;
		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		if (this.localName !== "wsp-remoteres-renderer-extended") this.reg.installSkin("wsp-remoteres-renderer-extended", this.shadowRoot);
		this._initAndInstallSkin(this.localName, init);

		if (this.config.openBtn != 'none') REG.reg.addToList(RemoteResRendererExtended.ACTIONS_LIST_BASE, 'openBtn', 1, OpenRemoteResAct.SINGLETON);

		this.buildStruct();
	}

	private buildStruct() {
		if (this.config.buildTitle !== 'none') this.titleRoot = <div id="title"/>;
		this.barActions = <BarActions class="horizontal" î={{
			reg: this.reg,
			actions: this.reg.mergeLists(RemoteResRendererExtended.ACTIONS_LIST_BASE, this.config.actionsList),
			actionContext: this,
			uiContext: "dialog",
			disableFullOverlay: true,
		} as OBarActionsInit<IRemoteResRendererActionCtx>}/> as BarActions<ACTCTX>;

		this.shadowRoot.appendChild(<div id="head">{this.titleRoot}{this.barActions}</div>);

		if (!this.config.preview || this.config.preview === 'shown') {
			this.remoteResRenderer = <RemoteResRenderer id="preview" î={BASIS.newInit(this.config.previewInit, this.reg)}/> as RemoteResRenderer;
		} else if (this.config.preview === 'tooltip') {
			POPUP.promiseTooltip(this, async (txtElt) => {
				//TODO
				return new RemoteResRenderer().initialize({
					reg: this.reg,
				})
			}, {hoverAllowed: true});
		}
		if (this.remoteResRenderer) this.shadowRoot.appendChild(this.remoteResRenderer);
	}

	protected async fetchResDescFromClient(url: string): Promise<JRemoteResDescFromClient> {
		return (await REMOTE.fetchResDesc(url, this.config.reg)) || {url, status: -1};
	}

	private _pendingRefresh: number;

	async _refresh() {
		const currentRefresh = this._pendingRefresh = Date.now();
		this.refreshUi();
		const newDesc = await this.config.fetchResDesc();
		if (currentRefresh != this._pendingRefresh) return;//raceCond
		this.remoteDesc = newDesc;
		this.refreshUi();
	}

	private refreshUi() {
		if (this.titleRoot) this.updateTitle();
		if (this.remoteResRenderer) this.updatePreview();
		if (this.barActions) this.updateBarActions();
	}

	/** Rafraichit la zone de Titre */
	private updateTitle() {
		if (typeof this.config.buildTitle === 'function' && this.config.buildTitle?.call(this, this.titleRoot, this.remoteDesc) == 'done') return;
		DOM.setTextContent(this.titleRoot, this.remoteDesc?.title || this.remoteDesc?.url || "[URL non renseignée]");
	}

	/** Rafraichit la zone de Preview */
	protected updatePreview() {
		const st = this.remoteDesc?.status || 0;
		if (st < 200 || st > 299) {
			DOM.setHiddenProp(this.remoteResRenderer, true);
			return;
		}
		DOM.setHiddenProp(this.remoteResRenderer, false);
		this.remoteResRenderer.setRemoteRes(this.remoteDesc ? this.config.remoteResSelector(this.config.reg, this.remoteDesc) : null);
	}

	/** Rafraichit la barre d'actions */
	private updateBarActions() {
		this.barActions.refreshContent();
	}

}

customElements.define('wsp-remoteres-renderer-extended', RemoteResRendererExtended);

REG.reg.registerSkin('wsp-remoteres-renderer-extended', 1, /* language=CSS */ `
	:host {
		flex: 1;
		flex-direction: column;
		display: flex;
	}

	[hidden] {
		display: none;
	}

	#head {
		border-bottom: 1px solid var(--border-color);
		display: flex;
	}

	#title {
		flex: 1;
	}

	c-bar-actions {
		flex: unset;
		min-height: min-content;
		min-width: min-content;
	}

	/*
		c-button {
			display: inline-flex;
			--icon-size: 1em;
		}*/

	#preview {
		display: inline-flex;
	}
`);


/**
 * Ouverture du gestionnaire des groupes
 */
class OpenRemoteResAct<CTX extends IRemoteResRendererActionCtx> extends Action<CTX> {
	static SINGLETON = new OpenRemoteResAct();

	constructor() {
		super("openBtn");
		this._label = "Ouvrir cette ressource...";
		this._icon = '/@skin@/core/widgets/remote/externalOpen.svg';
	}

	execute(ctx: CTX, ev?: Event): any {
		const view = this.findOpenView(ctx);
		if (view) window.open(REMOTE.buildUrlFromResDesc(ctx.remoteDesc, view), "_blank");
	}

	isVisible(ctx: CTX): boolean {
		if (this.findOpenView(ctx) == null) return false;
		return super.isVisible(ctx);
	}

	private findOpenView(ctx: CTX) {
		const editor = REMOTE.findEditorInResDesc(ctx.remoteDesc);
		if (editor) return editor;
		//Recherche d'une view complète différente de celle affichéee preview.
		const view = REMOTE.findFullViewInResDesc(ctx.remoteDesc);
		if (!view) return null;
		const previewTags = ctx.remoteResRenderer?.resShown?.getTags();
		return !LANG.arrayEquals(previewTags.body, view.tags?.body) || !LANG.arrayEquals(previewTags.web, view.tags?.web) ? view : null;
	}


}